/* One voice at a time. Native inline playback remains available without JS. */
(() => {
  const cleanups = new WeakMap();
  function init() {
    document.querySelectorAll('[data-hcb-ugc]').forEach(root => {
      if (cleanups.has(root)) return;
      const rail = root.querySelector('.hcb-ugc-rail');
      const nav = root.querySelector('.hcb-ugc-controls');
      const previous = nav.querySelector('[data-prev]');
      const next = nav.querySelector('[data-next]');
      const counter = nav.querySelector('[data-position]');
      const items = [...root.querySelectorAll('.hcb-ugc-card')].map(card => ({
        card, player:card.querySelector('.hcb-ugc-player'), video:card.querySelector('video'),
        play:card.querySelector('.hcb-ugc-play'), sound:card.querySelector('.hcb-ugc-sound'),
        error:card.querySelector('.hcb-ugc-error'), reason:null
      }));
      if (!items.length) return;
      const events = new AbortController();
      const on = (target, type, handler, options={}) => target.addEventListener(type, handler, {...options, signal:events.signal});
      const hover = matchMedia('(hover: hover) and (pointer: fine)');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let active = null, index = 0, request = 0, engaged = false, wantsSound = true, scrollTimer;

      function sync(item) {
        const playing = !item.video.paused && !item.video.ended;
        item.play.hidden = playing;
        item.sound.hidden = !playing;
        item.video.controls = playing;
        item.sound.textContent = item.video.muted ? 'Turn sound on' : 'Sound on';
        item.sound.setAttribute('aria-label', `${item.video.muted ? 'Turn on' : 'Mute'} sound: ${item.video.getAttribute('aria-label')}`);
        item.sound.setAttribute('aria-pressed', String(!item.video.muted));
        item.card.classList.toggle('is-current', playing);
      }
      function syncNavigation() {
        previous.disabled = index === 0;
        next.disabled = index === items.length - 1;
        counter.textContent = `${index + 1} / ${items.length}`;
        counter.setAttribute('aria-label', `Video ${index + 1} of ${items.length}`);
      }
      function stop() {
        request++;
        if (active) { active.video.pause(); sync(active); }
        active = null;
      }
      function reveal(item) {
        const viewport = rail.getBoundingClientRect(), box = item.card.getBoundingClientRect();
        if (box.left < viewport.left - 2 || box.right > viewport.right + 2) {
          rail.scrollTo({left:rail.scrollLeft + box.left - viewport.left - 2, behavior:'instant'});
        }
      }
      async function start(target, reason='manual', move=false) {
        const item = items[target];
        if (!item) return;
        const token = ++request;
        if (active && active !== item) { active.video.pause(); sync(active); }
        document.querySelectorAll('video').forEach(other => { if (other !== item.video && !other.paused) other.pause(); });
        active = item; index = target; item.reason = reason;
        if (reason !== 'hover') engaged = true;
        if (move) reveal(item);
        syncNavigation();
        item.error.hidden = true;
        if (item.video.ended) item.video.currentTime = 0;
        item.video.muted = !wantsSound;
        try {
          await item.video.play();
        } catch (error) {
          if (token !== request || active !== item) return;
          if (error.name === 'NotAllowedError' && !item.video.muted) {
            // Safari/Chrome may require an explicit tap before allowing audio.
            item.video.muted = true;
            try { await item.video.play(); }
            catch (mutedError) {
              if (token !== request || active !== item) return;
              if (mutedError.name !== 'AbortError' && mutedError.name !== 'NotAllowedError') item.error.hidden = false;
            }
          } else if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') item.error.hidden = false;
        }
        if (token !== request || active !== item) {
          if (active !== item) item.video.pause();
          return;
        }
        sync(item);
      }
      items.forEach((item, i) => {
        item.video.controls = false;
        item.play.hidden = false;
        on(item.play, 'click', () => start(i, 'manual'));
        on(item.sound, 'click', () => {
          wantsSound = item.video.muted;
          start(i, 'manual');
        });
        on(item.player, 'pointerenter', event => {
          if (event.pointerType === 'mouse' && hover.matches && !reduced.matches && (active !== item || item.video.paused)) start(i, 'hover');
        });
        on(item.player, 'pointerleave', event => {
          if (event.pointerType === 'mouse' && active === item && item.reason === 'hover') stop();
        });
        ['play','pause','volumechange'].forEach(type => on(item.video, type, () => sync(item)));
        on(item.video, 'ended', () => {
          sync(item);
          if (active === item && engaged && item.reason !== 'hover' && !reduced.matches && i < items.length - 1) start(i + 1, 'sequence', true);
        });
        on(item.video, 'error', () => { sync(item); item.error.hidden = false; });
      });
      on(previous, 'click', () => start(index - 1, 'manual', true));
      on(next, 'click', () => start(index + 1, 'manual', true));
      on(rail, 'keydown', event => {
        if (event.target !== rail || event.altKey || event.ctrlKey || event.metaKey) return;
        const destinations = {ArrowLeft:index - 1, ArrowRight:index + 1, Home:0, End:items.length - 1};
        if (!(event.key in destinations)) return;
        event.preventDefault();
        start(destinations[event.key], 'manual', true);
      });
      on(rail, 'scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const box = rail.getBoundingClientRect();
          const visible = items.map(item => {
            const r = item.card.getBoundingClientRect();
            return Math.max(0, Math.min(r.right, box.right) - Math.max(r.left, box.left)) / r.width;
          });
          if (visible[index] >= .6) return;
          const nearest = visible.indexOf(Math.max(...visible));
          if (nearest !== index) {
            if (engaged) start(nearest, 'sequence');
            else { index = nearest; syncNavigation(); }
          }
        }, 140);
      }, {passive:true});
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting && active?.video === entry.target) stop(); });
      }, {threshold:0}) : null;
      items.forEach(item => observer?.observe(item.video));
      on(document, 'visibilitychange', () => { if (document.hidden) { engaged = false; stop(); } });
      on(reduced, 'change', () => { if (reduced.matches && active?.reason === 'hover') stop(); });
      nav.hidden = false;
      syncNavigation();
      root.dataset.videoReady = 'true';
      cleanups.set(root, () => { stop(); clearTimeout(scrollTimer); observer?.disconnect(); events.abort(); cleanups.delete(root); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-hcb-ugc]').forEach(root => cleanups.get(root)?.()));
})();
